if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      variantResponseCache = new Map();
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.onOptionLabelClick = (event) => {
          const label = event.target.closest('variant-selects label[for]');
          if (!label || event.defaultPrevented || event.target.closest('a, button')) return;
          const input = document.getElementById(label.htmlFor);
          if (!input || input.type !== 'radio' || input.disabled || !this.contains(input)) return;
          // Native label activation focuses the clipped radio and can scroll to it.
          event.preventDefault();
          input.focus({ preventScroll: true });
          input.click();
        };
        this.addEventListener('click', this.onOptionLabelClick);

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onOptionLabelClick);
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const shouldSkipMediaUpdate = event.target?.hasAttribute('data-sky-option-reference-image') === true;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, { skipMediaUpdate: shouldSkipMediaUpdate }),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const controller = this.abortController;
        this.setAttribute('aria-busy', 'true');
        const priceStatus = this.querySelector('[data-sky-price-status]');
        let failed = false;
        if (priceStatus) { priceStatus.textContent = ''; priceStatus.hidden = true; }
        const response = this.variantResponseCache.has(requestUrl) && Date.now() - this.variantResponseCache.get(requestUrl).storedAt < 15000
          ? Promise.resolve(this.variantResponseCache.get(requestUrl).html)
          : fetch(requestUrl, { signal: controller.signal }).then((response) => {
              if (!response.ok) throw new Error('Unable to update product options');
              return response.text();
            });
        response
          .then((responseText) => {
            if (controller.signal.aborted) return;
            this.variantResponseCache.set(requestUrl, { html: responseText, storedAt: Date.now() });
            if (this.variantResponseCache.size > 12) {
              this.variantResponseCache.delete(this.variantResponseCache.keys().next().value);
            }
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            if (controller.signal.aborted) return;
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              failed = true;
              console.error(error);
              if (priceStatus) { priceStatus.textContent = 'Unable to update price. Please try again.'; priceStatus.hidden = false; }
            }
          })
          .finally(() => {
            if (this.abortController !== controller) return;
            this.removeAttribute('aria-busy');
            if (priceStatus) priceStatus.hidden = !failed;
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          // Keep the live picker and focused inputs in place. Replacing the whole
          // picker discards its expanded state and can trigger browser scroll anchoring.
          const current = this.variantSelectors;
          const incomingInputs = [...variantSelects.querySelectorAll('input[type="radio"]')];
          const currentInputs = [...current.querySelectorAll('input[type="radio"]')];
          const sameOptions = incomingInputs.length === currentInputs.length && incomingInputs.every(
            (input, index) => input.id === currentInputs[index].id && input.value === currentInputs[index].value
          );
          if (sameOptions && incomingInputs.length && !current.querySelector('select')) {
            incomingInputs.forEach((source, index) => {
              const destination = currentInputs[index];
              destination.className = source.className;
              destination.disabled = source.disabled;
              destination.checked = source.checked;
              destination.toggleAttribute('checked', source.checked);
              destination.setAttribute('form', source.getAttribute('form'));
            });
            const selectedData = current.querySelector('[data-selected-variant]');
            const incomingData = variantSelects.querySelector('[data-selected-variant]');
            if (selectedData && incomingData) selectedData.textContent = incomingData.textContent;
          } else {
            // Preserve the wrapper for dropdowns or a changed option structure too.
            const expanded = [...current.querySelectorAll('.sky-option-group')].map(
              (group) => group.classList.contains('sky-option-group--expanded')
            );
            variantSelects.querySelectorAll('.sky-option-group').forEach((group, index) => {
              if (!expanded[index]) return;
              group.classList.add('sky-option-group--expanded');
              group.querySelector('[data-sky-option-more]')?.setAttribute('aria-expanded', 'true');
              const more = group.querySelector('[data-more-label]');
              const less = group.querySelector('[data-less-label]');
              if (more) more.hidden = true;
              if (less) less.hidden = false;
            });
            HTMLUpdateUtility.setInnerHTML(current, variantSelects.innerHTML);
          }
        }
      }

      handleUpdateProductInfo(productUrl, options = {}) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (!options.skipMediaUpdate) {
            this.updateMedia(html, variant?.featured_media?.id);
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          { preventScroll: true }
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const currentValue = this.quantityInput.value ? parseInt(this.quantityInput.value) : null;
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        const shouldPreserveSkyQuantity =
          this.quantityInput.hasAttribute('data-sky-min') ||
          this.quantityInput.closest('.product-form__quantity')?.classList.contains('sky-tier-enabled');
        if (shouldPreserveSkyQuantity && Number.isFinite(currentValue) && currentValue > 0) {
          let nextValue = currentValue;
          if (nextValue < min) nextValue = min;
          if (max !== null && nextValue > max) nextValue = max;
          if (data.step > 1 && nextValue > min) {
            nextValue = min + Math.ceil((nextValue - min) / data.step) * data.step;
          }
          this.quantityInput.value = nextValue;
        } else {
          this.quantityInput.value = min;
        }

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!window.skyOptionMoreInitialized) {
  window.skyOptionMoreInitialized = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-sky-option-more]');
    if (!trigger) return;

    const optionGroup = trigger.closest('.sky-option-group--collapsible');
    if (!optionGroup) return;

    const isExpanded = optionGroup.classList.toggle('sky-option-group--expanded');
    trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

    const moreLabel = trigger.querySelector('[data-more-label]');
    const lessLabel = trigger.querySelector('[data-less-label]');
    if (moreLabel && lessLabel) {
      moreLabel.hidden = isExpanded;
      lessLabel.hidden = !isExpanded;
    }
  });
}

if (!window.skyOptionUnitSwitchInitialized) {
  window.skyOptionUnitSwitchInitialized = true;

  const SKY_OPTION_LENGTH_UNIT_STORAGE_KEY = 'sky_option_length_unit';
  const SKY_OPTION_VOLUME_DIAMETER_UNIT_STORAGE_KEY = 'sky_option_volume_diameter_unit';
  const SKY_OPTION_DEFAULT_UNITS = {
    length: 'in',
    'volume-diameter': 'oz',
  };
  let skyOptionUnitRefreshFrame = null;
  const skyOptionUnitCache = new Map();
  const skyOptionVolumeDiameterCache = new Map();

  const getStoredSkyOptionUnit = (unitGroup = 'length') => {
    const storageKey =
      unitGroup === 'volume-diameter'
        ? SKY_OPTION_VOLUME_DIAMETER_UNIT_STORAGE_KEY
        : SKY_OPTION_LENGTH_UNIT_STORAGE_KEY;
    const defaultUnit = SKY_OPTION_DEFAULT_UNITS[unitGroup] || SKY_OPTION_DEFAULT_UNITS.length;

    try {
      return window.localStorage.getItem(storageKey) || defaultUnit;
    } catch (error) {
      return defaultUnit;
    }
  };

  const setStoredSkyOptionUnit = (unit, unitGroup = 'length') => {
    const storageKey =
      unitGroup === 'volume-diameter'
        ? SKY_OPTION_VOLUME_DIAMETER_UNIT_STORAGE_KEY
        : SKY_OPTION_LENGTH_UNIT_STORAGE_KEY;

    try {
      window.localStorage.setItem(storageKey, unit);
    } catch (error) {
      // Unit switching is cosmetic. Ignore storage failures in private browsing.
    }
  };

  const normalizeSkyOptionText = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .replace(/\s*[xX*×]\s*/g, ' x ')
      .replace(/(\d)(in|inch|inches|cm|mm)\b/gi, '$1 $2')
      .trim();

  const formatSkyMillimeters = (value) => {
    const rounded = Math.round(value);
    return Number.isFinite(rounded) ? String(rounded) : '';
  };

  const formatSkyInches = (value) => {
    if (!Number.isFinite(value)) return '';
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded.toFixed(1));
  };

  const formatSkyFluidOunces = (value) => {
    if (!Number.isFinite(value)) return '';
    return String(Math.round(value));
  };

  const metricTextToMillimeters = (metricText) => {
    const normalized = normalizeSkyOptionText(metricText);
    const unitMatch = normalized.match(/(cm|mm)\b/i);
    if (!unitMatch) return '';

    const multiplier = unitMatch[1].toLowerCase() === 'cm' ? 10 : 1;
    const withoutUnit = normalized.replace(/(cm|mm)\b/i, '').trim();
    const converted = withoutUnit.replace(/\d+(?:\.\d+)?/g, (numberText) => {
      const number = Number.parseFloat(numberText);
      if (!Number.isFinite(number)) return numberText;
      return formatSkyMillimeters(number * multiplier);
    });

    return normalizeSkyOptionText(`${converted} mm`);
  };

  const convertImperialTailToMillimeters = (tailText) =>
    normalizeSkyOptionText(tailText).replace(/\d+(?:\.\d+)?\s*(?:in|inch|inches)\b/gi, (match) => {
      const number = Number.parseFloat(match);
      if (!Number.isFinite(number)) return match;
      return `${formatSkyMillimeters(number * 25.4)} mm`;
    });

  const convertMetricTailToInches = (tailText) =>
    normalizeSkyOptionText(tailText).replace(/\d+(?:\.\d+)?\s*(?:cm|mm)\b/gi, (match) => {
      const unitMatch = match.match(/(cm|mm)\b/i);
      const number = Number.parseFloat(match);
      if (!unitMatch || !Number.isFinite(number)) return match;

      const millimeters = unitMatch[1].toLowerCase() === 'cm' ? number * 10 : number;
      return `${formatSkyInches(millimeters / 25.4)} in`;
    });

  const compactSkyLengthBase = (baseText) =>
    normalizeSkyOptionText(baseText)
      .replace(/\s*(?:in|inch|inches|cm|mm)\b\s*$/i, '')
      .trim();

  const compactSkyLengthTail = (tailText) => {
    const normalized = normalizeSkyOptionText(tailText)
      .replace(/\s*\bflap\b/gi, '')
      .replace(/\s+([+])/g, ' $1')
      .trim();

    return normalized;
  };

  const parsePureMetricSkyLengthOptionValue = (raw) => {
    if (/(?:in|inch|inches)\b/i.test(raw)) return null;

    const unitMatch = raw.match(/(cm|mm)\b/i);
    if (!unitMatch) return null;

    const unit = unitMatch[1].toLowerCase();
    const multiplier = unit === 'cm' ? 10 : 1;
    const withoutUnit = normalizeSkyOptionText(raw.replace(new RegExp(`\\s*${unit}\\b`, 'gi'), ''));
    const numbers = withoutUnit.match(/\d+(?:\.\d+)?/g) || [];
    if (numbers.length < 2) return null;

    const millimeterLabel = withoutUnit.replace(/\d+(?:\.\d+)?/g, (numberText) => {
      const number = Number.parseFloat(numberText);
      if (!Number.isFinite(number)) return numberText;
      return formatSkyMillimeters(number * multiplier);
    });
    const inchLabel = withoutUnit.replace(/\d+(?:\.\d+)?/g, (numberText) => {
      const number = Number.parseFloat(numberText);
      if (!Number.isFinite(number)) return numberText;
      return formatSkyInches((number * multiplier) / 25.4);
    });

    return {
      in: normalizeSkyOptionText(`${inchLabel} in`),
      mm: normalizeSkyOptionText(`${millimeterLabel} mm`),
    };
  };

  const parseSkyLengthOptionValue = (rawValue) => {
    const raw = normalizeSkyOptionText(rawValue);
    if (!raw) return null;
    if (skyOptionUnitCache.has(raw)) return skyOptionUnitCache.get(raw);

    const parenthetical = raw.match(/\(([^)]*(?:cm|mm|in|inch|inches)[^)]*)\)/i);
    if (!parenthetical) {
      const parsedMetricOnly = parsePureMetricSkyLengthOptionValue(raw);
      skyOptionUnitCache.set(raw, parsedMetricOnly);
      return parsedMetricOnly;
    }

    const before = normalizeSkyOptionText(raw.slice(0, parenthetical.index));
    const inside = normalizeSkyOptionText(parenthetical[1]);
    const after = normalizeSkyOptionText(raw.slice(parenthetical.index + parenthetical[0].length));

    const beforeIsImperial = /(?:in|inch|inches)\b/i.test(before);
    const beforeIsMetric = /(?:cm|mm)\b/i.test(before);
    const insideIsImperial = /(?:in|inch|inches)\b/i.test(inside);
    const insideIsMetric = /(?:cm|mm)\b/i.test(inside);

    let imperialBase = '';
    let metricBase = '';

    if (beforeIsImperial && insideIsMetric) {
      imperialBase = before;
      metricBase = inside;
    } else if (beforeIsMetric && insideIsImperial) {
      imperialBase = inside;
      metricBase = before;
    } else {
      skyOptionUnitCache.set(raw, null);
      return null;
    }

    const imperialTail = beforeIsMetric && insideIsImperial ? convertMetricTailToInches(after) : after;
    const imperialLabel = normalizeSkyOptionText(
      [compactSkyLengthBase(imperialBase), compactSkyLengthTail(imperialTail)].filter(Boolean).join(' ')
    );
    const metricLabel = normalizeSkyOptionText(
      [
        compactSkyLengthBase(metricTextToMillimeters(metricBase)),
        compactSkyLengthTail(convertImperialTailToMillimeters(after)),
      ]
        .filter(Boolean)
        .join(' ')
    );

    const parsed = metricLabel
      ? {
          in: imperialLabel,
          mm: metricLabel,
        }
      : null;

    skyOptionUnitCache.set(raw, parsed);
    return parsed;
  };

  const getSkyOptionLabelRawValue = (label) => {
    if (label.dataset.skyOptionRawValue) return label.dataset.skyOptionRawValue;
    if (label.tagName === 'OPTION') return label.value || label.textContent;
    return label.textContent;
  };

  const parseSkyVolumeDiameterOptionValue = (rawValue) => {
    const raw = normalizeSkyOptionText(rawValue);
    if (!raw) return null;
    if (skyOptionVolumeDiameterCache.has(raw)) return skyOptionVolumeDiameterCache.get(raw);

    const fluidOunceMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:fl\s*)?oz\b/i);
    const capacityMatch = raw.match(/(\d+(?:\.\d+)?)\s*ml\b/i);
    const diameterMatch = raw.match(/(\d+(?:\.\d+)?)\s*(mm|cm)\b/i);
    if (!capacityMatch || (!fluidOunceMatch && !diameterMatch)) {
      skyOptionVolumeDiameterCache.set(raw, null);
      return null;
    }

    const capacityMl = Number.parseFloat(capacityMatch[1]);
    const diameterValue = diameterMatch ? Number.parseFloat(diameterMatch[1]) : null;
    const diameterUnit = diameterMatch ? diameterMatch[2].toLowerCase() : null;
    if (!Number.isFinite(capacityMl) || (diameterMatch && !Number.isFinite(diameterValue))) {
      skyOptionVolumeDiameterCache.set(raw, null);
      return null;
    }

    const diameterMm = diameterMatch ? (diameterUnit === 'cm' ? diameterValue * 10 : diameterValue) : null;
    const imperialDiameter = diameterMatch ? ` / ${formatSkyInches(diameterMm / 25.4)} in` : '';
    const metricDiameter = diameterMatch ? ` / ${formatSkyMillimeters(diameterMm)} mm` : '';
    const parsed = {
      oz: `${
        fluidOunceMatch
          ? fluidOunceMatch[1]
          : formatSkyFluidOunces(capacityMl / 29.5735)
      } oz${imperialDiameter}`,
      ml: `${formatSkyMillimeters(capacityMl)} ml${metricDiameter}`,
    };

    skyOptionVolumeDiameterCache.set(raw, parsed);
    return parsed;
  };

  const applySkyOptionUnits = (root = document) => {
    root.querySelectorAll('[data-sky-unit-group]').forEach((optionGroup) => {
      const unitGroup = optionGroup.dataset.skyUnitGroup || 'length';
      const selectedUnit =
        unitGroup === 'volume-diameter'
          ? getStoredSkyOptionUnit(unitGroup) === 'ml'
            ? 'ml'
            : 'oz'
          : getStoredSkyOptionUnit(unitGroup) === 'mm'
            ? 'mm'
            : 'in';
      const parseValue =
        unitGroup === 'volume-diameter' ? parseSkyVolumeDiameterOptionValue : parseSkyLengthOptionValue;
      const labels = Array.from(optionGroup.querySelectorAll('[data-sky-option-label]'));
      const hasConvertibleLabel = labels.some((label) => parseValue(getSkyOptionLabelRawValue(label)));
      const toggle = optionGroup.querySelector(`[data-sky-unit-toggle="${unitGroup}"]`);

      if (toggle) {
        toggle.hidden = !hasConvertibleLabel;
        toggle.querySelectorAll('[data-sky-unit-button]').forEach((button) => {
          button.setAttribute('aria-pressed', button.dataset.skyUnitButton === selectedUnit ? 'true' : 'false');
        });
      }

      labels.forEach((label) => {
        const rawValue = getSkyOptionLabelRawValue(label);
        const parsed = parseValue(rawValue);
        label.textContent = parsed ? parsed[selectedUnit] : rawValue;
      });
    });
  };

  const requestSkyOptionUnitRefresh = (root = document) => {
    if (skyOptionUnitRefreshFrame) window.cancelAnimationFrame(skyOptionUnitRefreshFrame);
    skyOptionUnitRefreshFrame = window.requestAnimationFrame(() => {
      skyOptionUnitRefreshFrame = null;
      applySkyOptionUnits(root);
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-sky-unit-button]');
    if (!button) return;

    const unitGroup = button.closest('[data-sky-unit-group]')?.dataset.skyUnitGroup || 'length';
    const unit = button.dataset.skyUnitButton;
    const validUnit =
      unitGroup === 'volume-diameter' ? unit === 'oz' || unit === 'ml' : unit === 'in' || unit === 'mm';
    if (!validUnit) return;

    setStoredSkyOptionUnit(unit, unitGroup);
    requestSkyOptionUnitRefresh();
  });

  document.addEventListener('DOMContentLoaded', () => requestSkyOptionUnitRefresh());
  document.addEventListener('product-info:loaded', (event) => requestSkyOptionUnitRefresh(event.target));
  document.addEventListener('shopify:section:load', (event) => requestSkyOptionUnitRefresh(event.target));

  const skyOptionUnitObserver = new MutationObserver((mutations) => {
    if (
      mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.('variant-selects, product-info') || node.querySelector?.('variant-selects'))
        )
      )
    ) {
      requestSkyOptionUnitRefresh();
    }
  });

  skyOptionUnitObserver.observe(document.documentElement, { childList: true, subtree: true });
  requestSkyOptionUnitRefresh();
}
